// Loads of the lecture tree and the flat lecture previews into the
// lectureTree and lectures slices, with the request-order guards both slices
// rely on (ora-fe ADR-0002: the requests live in `src/services/lectures.ts`).
import {
  getLectureTree,
  postBulkLecturePreviews,
} from '../../services/lectures';
import type { RootState } from '../../store/store';
import type { LecturePreview, LectureTreeResponse } from '../../types/Lecture';
import { examsSelectors } from '../exams/examsSlice';
import {
  treeFailed,
  treeLoading,
  treeReceived,
  treeRequestAbandoned,
} from '../lectureTree/lectureTreeSlice';
import {
  treeDocumentToLecturePreview,
  type LectureThunk,
} from './lectureThunks';
import {
  lecturesFailed,
  lecturesLoading,
  lecturesReceived,
  lecturesReceivedForExam,
  lecturesRequestAbandoned,
  lecturesUpsertMany,
  lecturesUpsertManyPreservingSelection,
} from './lecturesSlice';

let nextLecturePreviewRequestId = 0;

/**
 * The exam whose lectures the panel is currently showing: the active custom
 * exam, else the primary exam when it is a custom one.
 */
export const selectCurrentLectureExamId = (
  state: RootState
): string | undefined => {
  const activeCustomExam = examsSelectors.selectActiveCustomExam(state);
  const primaryExam = examsSelectors.selectPrimaryExam(state);

  return (
    activeCustomExam?.id ||
    (primaryExam?.type === 'CUSTOM' ? primaryExam.id : undefined)
  );
};

export const fetchLectureTree =
  ({
    examId,
    skipLoadingState,
    onlyIfStillCurrent,
    preserveSelection,
  }: {
    examId: string;
    /** When true, refresh in place instead of blanking the panel into a skeleton */
    skipLoadingState?: boolean;
    /** When true, discard the response if the active lecture exam changed */
    onlyIfStillCurrent?: boolean;
    /**
     * When true, upsert the tree and its document previews WITHOUT re-running
     * selection hydration. Post-mutation refreshes (rename/hide/retry/delete) use
     * this so an in-session selection — and its pending debounced write — is not
     * wiped by the server's older `selected` flags.
     */
    preserveSelection?: boolean;
  }): LectureThunk<LectureTreeResponse> =>
  async (dispatch, getState) => {
    const requestId = ++nextLecturePreviewRequestId;
    const treeState = getState().lectureTree;

    const previousLecturesRequestId =
      getState().lectures?.latestRequestId ?? null;

    const isAlreadyShowingExam =
      treeState.examId === examId && treeState.status === 'succeeded';

    const shouldShowLoading = Boolean(
      !skipLoadingState && !preserveSelection && !isAlreadyShowingExam
    );

    // Always claim the request id so a stale response is dropped, even when this
    // refresh renders no loading state. A claim this request never releases (see
    // treeRequestAbandoned) would drop an OLDER response that is still in flight
    // and wedge the panel in `loading` on the wrong exam's nodes.
    const previousTreeRequestId = treeState.latestRequestId ?? null;

    dispatch(
      treeLoading({ examId, requestId, background: !shouldShowLoading })
    );

    const abandonRequest = () => {
      dispatch(
        treeRequestAbandoned({
          requestId,
          previousRequestId: previousTreeRequestId,
        })
      );

      // The lectures slice claims the id too — but only when this refresh
      // renders a loading state — so its claim needs the same release. Left
      // pinned, `loadingExamId` makes the slice drop every later response for a
      // different exam, including the refreshes that never dispatch
      // `lecturesLoading` (ORA-498).
      if (shouldShowLoading) {
        dispatch(
          lecturesRequestAbandoned({
            requestId,
            previousRequestId: previousLecturesRequestId,
          })
        );
      }
    };

    // The lectures slice applies responses in request order too, so a background
    // refresh must identify itself rather than opt out of the staleness guard.
    const trackedRequestId = requestId;

    if (shouldShowLoading) {
      dispatch(lecturesLoading({ examId, requestId }));
    }

    try {
      const { tree, collapsedLectureFolderIds, hasCustomizedLectureSelection } =
        await getLectureTree(examId);

      if (
        onlyIfStillCurrent &&
        selectCurrentLectureExamId(getState()) !== examId
      ) {
        abandonRequest();
        return tree;
      }

      const documentPreviews = tree.nodes.flatMap((node, index) =>
        node.nodeType === 'DOCUMENT'
          ? [treeDocumentToLecturePreview(node, tree.exam, index)]
          : []
      );

      dispatch(
        treeReceived({
          examId: tree.exam.id,
          nodes: tree.nodes,
          requestId,
          // Folders the member collapsed on an earlier visit (server-kept,
          // ORA-567); only the first tree of an exam reads it, a refresh keeps
          // the session state.
          collapsedIds: collapsedLectureFolderIds,
        })
      );

      // `treeReceived` drops a response that is stale (a newer request landed)
      // or was issued before a local mutation; the flat collection has no such
      // guard of its own, so mirror the tree's verdict here — otherwise the
      // preview panel keeps an older exam state than the tree, or a just-deleted
      // document is re-inserted as a ghost.
      const treeAfter = getState().lectureTree;

      const treeDroppedResponse =
        (treeAfter.latestRequestId !== null &&
          requestId < treeAfter.latestRequestId) ||
        (treeAfter.responseCutoffRequestId !== null &&
          treeAfter.responseCutoffRequestId !== undefined &&
          requestId <= treeAfter.responseCutoffRequestId);

      if (treeDroppedResponse) {
        return tree;
      }

      if (preserveSelection) {
        dispatch(lecturesUpsertManyPreservingSelection(documentPreviews));
      } else {
        dispatch(
          lecturesReceivedForExam({
            examId: tree.exam.id,
            lectures: documentPreviews,
            requestId: trackedRequestId,
            hasCustomizedLectureSelection,
          })
        );
      }

      return tree;
    } catch (error) {
      abandonRequest();

      // A background/post-mutation refresh must not tear down the rendered tree;
      // the caller surfaces its own failure.
      if (shouldShowLoading) {
        dispatch(treeFailed());

        dispatch(
          lecturesFailed(
            error instanceof Error ? error.message : 'Failed to load lectures'
          )
        );
      }

      throw error;
    }
  };

export const fetchBulkLecturePreviews =
  ({
    examId,
    lectureIds,
    page,
    limit,
    paginate,
    skipLoadingState,
    onlyIfStillCurrent,
  }: {
    examId?: string;
    lectureIds?: string[];
    page?: number;
    limit?: number;
    /** When true, do not replace the whole list for page > 1 */
    paginate?: boolean;
    /** When true, skip dispatching lecturesLoading (for background refreshes) */
    skipLoadingState?: boolean;
    /** When true, discard the response if the active lecture exam changed */
    onlyIfStillCurrent?: boolean;
  }): LectureThunk<LecturePreview[]> =>
  async (dispatch, getState) => {
    const shouldReplaceLectures = !paginate || !page || page <= 1;

    const requestId = shouldReplaceLectures
      ? ++nextLecturePreviewRequestId
      : undefined;

    const trackedRequestId = skipLoadingState ? undefined : requestId;

    // Only show top-level loading state when not appending pages and not skipping
    if (shouldReplaceLectures && trackedRequestId !== undefined) {
      dispatch(lecturesLoading({ examId, requestId: trackedRequestId }));
    }

    const { lectures, hasCustomizedLectureSelection } =
      await postBulkLecturePreviews({ examId, lectureIds, page, limit });

    if (paginate && page && page > 1) {
      dispatch(lecturesUpsertMany(lectures));
    } else if (examId) {
      if (
        onlyIfStillCurrent &&
        selectCurrentLectureExamId(getState()) !== examId
      ) {
        return lectures;
      }

      dispatch(
        lecturesReceivedForExam({
          examId,
          lectures,
          requestId: trackedRequestId,
          hasCustomizedLectureSelection,
        })
      );
    } else {
      dispatch(lecturesReceived(lectures));
    }
    return lectures;
  };
